import type { ConsentStatusT, ParticipantT } from '@/types/meeting';

import { Maybe } from '@/types';
import { AnalysisConfig, UIVersion } from '@/types/config';

type ParticipantConsentFields = Pick<
  ParticipantT,
  | 'consentStatus'
  | 'audioConsentStatus'
  | 'videoConsentStatus'
  | 'voiceMatchConsentStatus'
  | 'locationConsentStatus'
>;

interface AnalysisConfigs {
  audio_liveness_config?: AnalysisConfig | null;
  video_liveness_config?: AnalysisConfig | null;
  voice_match_config?: AnalysisConfig | null;
  location_config?: AnalysisConfig | null;
}

export enum GuestAppFlow {
  NONE = 'none',
  CONSENT = 'consent',
  LOCATION = 'location',
}

export enum InvitationKind {
  CONSENT = 'consent',
  LOCATION = 'location',
}

export enum ConsentPreset {
  FULL = 'full',
  MINIMAL = 'minimal',
}

export function configEqual(
  a: AnalysisConfig | null | undefined,
  b: AnalysisConfig | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.enabled === b.enabled && a.consent_required === b.consent_required;
}

export function anyFeatureRequiresConsent(configs: AnalysisConfigs): boolean {
  return [
    configs.audio_liveness_config,
    configs.video_liveness_config,
    configs.voice_match_config,
    configs.location_config,
  ].some((cfg) => !!cfg?.enabled && !!cfg?.consent_required);
}

const CONSENT_REGION_GLOBAL = 'GLOBAL';
const NOT_PREFIX = 'NOT ';

function regionsRequireConsent(
  regions: NonNullable<AnalysisConfig['consent_required_regions']>,
  regionCodes: string[],
): boolean {
  const require = regions.require ?? [];
  const except = regions.except ?? [];

  const isGlobal = require.length === 1 && require[0] === CONSENT_REGION_GLOBAL;

  if (isGlobal) {
    return !except.some((entry) => regionCodes.includes(entry));
  }

  if (except.some((entry) => regionCodes.includes(entry))) {
    return false;
  }

  const [countryCode] = regionCodes;

  return require.some((entry) => {
    if (regionCodes.includes(entry)) return true;

    if (entry.startsWith(NOT_PREFIX)) {
      return countryCode !== entry.slice(NOT_PREFIX.length);
    }

    return false;
  });
}

export function isConsentRequiredForRegion(
  cfg: AnalysisConfig | null | undefined,
  regionCodes: string[],
): boolean {
  if (!cfg?.enabled || !cfg.consent_required) return false;

  const regions = cfg.consent_required_regions;

  if (!regions || !regions.require?.length) return true;

  // No resolved region (geolocation pending) defaults to requiring consent.
  if (!regionCodes.length) return true;

  return regionsRequireConsent(regions, regionCodes);
}

export function isConsentRequiredForParticipant(
  config: AnalysisConfigs,
  regionCodes: string[],
): boolean {
  return [
    config.audio_liveness_config,
    config.video_liveness_config,
    config.voice_match_config,
    config.location_config,
  ].some((cfg) => isConsentRequiredForRegion(cfg, regionCodes));
}

export function getRegionCodes(
  geo?: { country_code?: string; subdivision_code?: string } | null,
): string[] {
  if (!geo?.country_code) return [];

  if (!geo.subdivision_code) return [geo.country_code];

  const region = geo.subdivision_code.startsWith(`${geo.country_code}-`)
    ? geo.subdivision_code
    : `${geo.country_code}-${geo.subdivision_code}`;

  return [geo.country_code, region];
}

// A participant can require consent for one analysis type but not another
// (PD-67961), so "still needs a consent decision" can no longer be a single
// consentStatus === 'NotReceived' check — it's true if ANY of the four
// per-feature statuses is still NotReceived. Falls back to the aggregate
// consentStatus when none of the per-feature fields are present yet (backend
// hasn't started sending them, or a participant record predates the split).
export function hasPendingConsent(
  participant: ParticipantConsentFields,
): boolean {
  const perFeatureStatuses = [
    participant.audioConsentStatus,
    participant.videoConsentStatus,
    participant.voiceMatchConsentStatus,
    participant.locationConsentStatus,
  ];

  if (perFeatureStatuses.every((status) => status === undefined)) {
    return participant.consentStatus === 'NotReceived';
  }

  return perFeatureStatuses.some((status) => status === 'NotReceived');
}

// The backend no longer sends the aggregate consent_status (PD-67961) — it's
// computed client-side instead, mirroring the backend's own now-internal
// store.DeriveConsentStatus. Priority order, most conservative first: any Denied
// wins outright; otherwise any still-NotReceived means the participant hasn't
// finished responding; otherwise any explicit Granted takes precedence over
// NotRequired; only NotRequired across all four yields NotRequired. Falls back to
// the legacy aggregate when none of the four per-feature fields are present (a
// participant record predates the split).
export function deriveConsentStatus(
  audioConsentStatus: ConsentStatusT | undefined,
  videoConsentStatus: ConsentStatusT | undefined,
  voiceMatchConsentStatus: ConsentStatusT | undefined,
  locationConsentStatus: ConsentStatusT | undefined,
  legacyConsentStatus?: ConsentStatusT,
): ConsentStatusT {
  const perFeatureStatuses = [
    audioConsentStatus,
    videoConsentStatus,
    voiceMatchConsentStatus,
    locationConsentStatus,
  ];

  if (perFeatureStatuses.every((status) => status === undefined)) {
    return legacyConsentStatus ?? 'NotReceived';
  }

  if (perFeatureStatuses.includes('Denied')) return 'Denied';
  if (perFeatureStatuses.includes('NotReceived')) return 'NotReceived';
  if (perFeatureStatuses.includes('Granted')) return 'Granted';

  return 'NotRequired';
}

interface InvitationContext {
  guestAppFlow: GuestAppFlow;
  consentEnabled: boolean;
  locationEnabled: boolean;
  // Participant ids that were already sent an invitation.
  consentSent: string[];
}

// Decides which invitation a participant needs, used by the Zoom in-meeting
// invitation hook: CONSENT for a pending decision, LOCATION when only
// the location capture is missing (e.g. consent was granted by long-term consent
// at join time, so the participant never opened the app), null when none is needed.
export function getInvitationKind(
  participant: ParticipantT,
  context: InvitationContext,
): InvitationKind | null {
  const { guestAppFlow, consentEnabled, locationEnabled, consentSent } =
    context;

  if (participant.name.toLowerCase().includes('security assistant')) {
    return null;
  }

  // Exclude disconnected participants
  if (participant.disconnected) {
    return null;
  }

  if (consentEnabled && hasPendingConsent(participant)) {
    if (participant.ipRisk) {
      return null;
    }
  }

  let kind = InvitationKind.LOCATION;

  if (guestAppFlow === GuestAppFlow.CONSENT) {
    if (hasPendingConsent(participant)) {
      // Only invite participants still awaiting a decision on at least one
      // required feature (PD-67961) — a region can exempt some features but not
      // others.
      kind = InvitationKind.CONSENT;
    } else if (
      !locationEnabled ||
      participant.ipRisk ||
      deriveConsentStatus(
        participant.audioConsentStatus,
        participant.videoConsentStatus,
        participant.voiceMatchConsentStatus,
        participant.locationConsentStatus,
        participant.consentStatus,
      ) === 'Denied'
    ) {
      return null;
    }
    // Otherwise consent is already resolved but the participant never opened the
    // app, so no location was captured: ask for the location only. A consent
    // invitation would be pre-filtered out server-side.
  } else if (participant.ipRisk) {
    // guestAppFlow is LOCATION here — no feature requires an explicit consent
    // decision (hasPendingConsent is always false), but the participant still
    // needs to open the app once so their location can be captured. ipRisk
    // populates once that's done.
    return null;
  }

  // Exclude participants who have already been sent consent requests
  if (consentSent.includes(participant.id)) {
    return null;
  }

  return kind;
}

export function getActionRecipientIds(
  participants: Pick<ParticipantT, 'id' | 'isBotRequester'>[],
): string[] {
  return participants
    .filter((p) => Boolean(p.id) && !p.isBotRequester)
    .map((p) => p.id);
}

export function getGuestAppFlow(configs: Maybe<AnalysisConfigs>): GuestAppFlow {
  if (!configs) {
    return GuestAppFlow.NONE;
  }

  const anyConsentRequired = anyFeatureRequiresConsent(configs);

  if (anyConsentRequired) {
    return GuestAppFlow.CONSENT;
  }

  if (configs.location_config?.enabled) {
    return GuestAppFlow.LOCATION;
  }

  return GuestAppFlow.NONE;
}

function isFullConsentPreset(configs: AnalysisConfigs): boolean {
  return [
    configs.audio_liveness_config,
    configs.video_liveness_config,
    configs.location_config,
  ].every((cfg) => !!cfg?.enabled && !!cfg?.consent_required);
}

export function getConsentPreset(
  configs?: Maybe<AnalysisConfigs>,
): ConsentPreset | null {
  if (!configs) {
    return null;
  }

  return isFullConsentPreset(configs)
    ? ConsentPreset.FULL
    : ConsentPreset.MINIMAL;
}

export function buildConsentMessageText(
  screenName: string,
  entity: string,
  uiVersion: UIVersion,
): string {
  const v1Message = `${screenName} is requesting your ${entity} for this meeting`;
  const messageText =
    uiVersion === UIVersion.V1
      ? v1Message
      : `${screenName} is requesting you to complete the security verification request`;

  if (messageText.length > 90) {
    return uiVersion === UIVersion.V1
      ? v1Message
      : 'Meeting host is requesting you to complete the security verification request';
  }

  return messageText;
}

export function getUiVersion(configs?: Maybe<AnalysisConfigs>) {
  // TEMP - remove this once we've fully rolled out the new UI'
  if (!configs) {
    return UIVersion.V1;
  }

  const preset = getConsentPreset(configs);

  if (preset === ConsentPreset.FULL) {
    return UIVersion.V1;
  }

  return UIVersion.V2;
}
